// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseInputSearch,
  BaseMoreAction,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
import { EquipmentFormModal } from '~/components/module/equipment';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_EQUIPMENT,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, ISortBy } from '~/utils/interface/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { equipmentActions } from '~/thunks/equipment/equipmentSlice';
import { EQUIPMENT_PERMISSIONS } from '~/utils/constants/permissions';
import { IEquipment, IQueryParamEquipment } from '~/utils/interface/equipment';
import { deleteEquipment, getListEquipment } from '~/thunks/equipment/equipmentThunk';
// Styles, images, icons
import styles from './equipment.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsEquipment = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onEdit: (id: string) => void,
  onArchive: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IEquipment>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IEquipment) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.name}</Box>
        </Stack>
      ),
    },
    {
      id: 'equipmentType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_equipment_type')}</Box>
          <SortComponent name='equipment type' sortType={getSortType('equipment type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IEquipment) => <Box className={cx('textValue')}>{record?.equipmentType || EMPTY_VALUE}</Box>,
    },
    {
      id: 'rentalStatus',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_rental_status')}</Box>
          <SortComponent name='rental status' sortType={getSortType('rental status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IEquipment) => <Box className={cx('textValue')}>{record?.rentalStatus || EMPTY_VALUE}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IEquipment) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'note',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_note')}</Box>
          <SortComponent name='note' sortType={getSortType('note')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IEquipment) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.note || EMPTY_VALUE}</Box>
        </Stack>
      ),
    },
    {
      id: 'action',
      label: t('common_text_action', { ns: 'common/base' }),
      minWidth: 100,
      align: 'center',
      render: (record: IEquipment) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onArchive(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Equipment = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateEquipments, canViewEquipments, canEditEquipments, canDeleteEquipments } =
    usePermissions(EQUIPMENT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/equipment', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isRefreshEquipmentList } = useAppSelector((state) => state.equipment);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_EQUIPMENT);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamEquipment>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [idsEquipment, setIdsEquipment] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<IEquipment[]>([]);
  const [idEquipment, setIdEquipment] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isOpenEquipmentFormModal, setIsOpenEquipmentFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListEquipment(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshEquipmentList) return;

    handleGetListEquipment(paramObject);

    dispatch(equipmentActions.setRefreshEquipmentList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshEquipmentList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListEquipment = (params: IQueryParamEquipment) => {
    setIsLoading(true);

    dispatch(getListEquipment(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setEquipmentList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdEquipment(id);
    setIsOpenEquipmentFormModal(true);
  };

  const handleArchive = (id: string) => {
    if (!id) return;

    setIdEquipment(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdEquipment(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteEquipment(idEquipment))
      .unwrap()
      .then(() => {
        setIdEquipment(EMPTY_STRING);
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowsSelect = (ids: string[], selectedItems?: IEquipment[]) => {
    setIdsEquipment(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowEquipmentFormModal = () => {
    setIsOpenEquipmentFormModal((prev) => !prev);
    setIdEquipment(EMPTY_STRING);
  };
  //#endregion Handle Function

  return (
    <Box id='equipmentPage' className={cx('container')}>
      <BaseToolbar title={t('equipment_title_management')}>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateEquipments && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowEquipmentFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('equipment_text_add_equipment')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewEquipments ? equipmentList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsEquipment(
            t,
            sortBy,
            handleSort,
            handleEdit,
            handleArchive,
            canEditEquipments,
            canDeleteEquipments
          )}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsEquipment}
          // onSelectChange={handleRowsSelect}
          {...(canEditEquipments ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isOpenEquipmentFormModal && (
        <EquipmentFormModal onClose={handleShowEquipmentFormModal} idEquipment={idEquipment} />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Equipment;
